import * as React from 'react';
import type {ReactableColumnDef} from '@deviceinsight/ng-ui-components';
import {Checkbox} from '@deviceinsight/ng-ui-basic-components';
import type {Pageable} from '@deviceinsight/ng-ui-api-client';
import type {SearchConfig} from './types';
import FilterTable from './FilterTable';
type Props<T> = {
	searchContext: string;
	searchConfig: SearchConfig<T>;
	columnDefinitions: Array<ReactableColumnDef<T>>;
	pageSize: number;
	uniqueItemKey: (arg0: T) => any;
	onSelectSome: (items: Array<any>) => any;
	initiallySelectedItems?: Array<any>;
	returnEntireItem?: boolean;
	onSelectAll?: (query: string, count: number) => any;
	updateTrigger?: any;
	withSync?: {
		itemsToSync: number;
		expectedTotalAfterSync: number;
		syncCompleted: () => any;
	};
};
type State = {
	selectedItems: Array<any>;
	allItemsSelected: boolean;
	query: string | null | undefined;
	searchResult: Pageable<any> | null | undefined;
};
export default class BulkSelectFilterTable extends React.Component<Props<any>, State> {
	static defaultProps = {
		returnEntireItem: false
	};
	state: State = {
		selectedItems: this.props.initiallySelectedItems || [],
		allItemsSelected: false,
		query: undefined,
		searchResult: undefined
	};

	componentDidUpdate(prevProps: Props<any>) {
		const {updateTrigger, onSelectSome, initiallySelectedItems = []} = this.props;

		if (updateTrigger && updateTrigger !== prevProps.updateTrigger) {
			this.setState({
				selectedItems: initiallySelectedItems,
				allItemsSelected: false
			});
			onSelectSome(initiallySelectedItems);
		}
	}

	itemIsSelected = (item: any): boolean => {
		const {returnEntireItem} = this.props;
		const {selectedItems, allItemsSelected} = this.state;

		if (allItemsSelected) {
			return true;
		} else if (returnEntireItem) {
			// $FlowIgnore
			return selectedItems.find((selectedItem) => selectedItem.id === item.id) !== undefined;
		} else {
			return selectedItems.includes(item.id);
		}
	};
	getColumnDefinitions = (): Array<ReactableColumnDef<any>> => {
		const {columnDefinitions, onSelectSome, onSelectAll} = this.props;
		const {selectedItems, allItemsSelected, query, searchResult} = this.state;
		return [
			{
				name: 'checkbox',
				label: onSelectAll ? (
					<Checkbox
						value={allItemsSelected}
						onChange={(checked) => {
							this.setState({
								selectedItems: [],
								allItemsSelected: checked
							});

							if (checked && query != null && searchResult) {
								onSelectAll(query, searchResult.totalElements);
							} else {
								onSelectSome([]);
							}
						}}
						title={{
							id: 'components.filtertable.selectAllItems',
							defaultValue: 'Select/deselect all items in search result'
						}}
					/>
				) : (
					''
				),
				sortable: false,
				render: (item) => (
					<Checkbox
						value={this.itemIsSelected(item)}
						onChange={(checked) => {
							const {returnEntireItem} = this.props;
							let newSelectedItems;

							if (returnEntireItem) {
								newSelectedItems = checked
									? [...selectedItems, item] // $FlowIgnore
									: selectedItems.filter((selectedItem) => selectedItem.id !== item.id);
							} else {
								newSelectedItems = checked
									? [...selectedItems, item.id]
									: selectedItems.filter((id) => id !== item.id);
							}

							this.setState({
								selectedItems: newSelectedItems
							});
							onSelectSome(newSelectedItems);
						}}
						title={{
							id: 'components.filtertable.selectOneItem',
							defaultValue: 'Select/deselect item'
						}}
						disabled={allItemsSelected}
					/>
				),
				width: '1%'
			},
			...columnDefinitions
		];
	};
	handleSearchUpdate = (query: string, searchResult: Pageable<any>) => {
		this.setState({
			query,
			searchResult
		});

		if (this.state.allItemsSelected && this.props.onSelectAll) {
			this.props.onSelectAll(query, searchResult.totalElements);
		}
	};

	render() {
		return (
			<FilterTable
				{...this.props}
				columnDefinitions={this.getColumnDefinitions()}
				onUpdate={this.handleSearchUpdate}
			/>
		);
	}
}
